import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { AreaChart, Area, CartesianGrid, XAxis } from 'recharts'
import { 
  MessageCircle, 
  ShoppingCart, 
  Target, 
  BarChart3, 
  Users, 
  Globe, 
  Zap,
  ArrowRight,
  Eye,
  Activity,
  PieChart,
  Lightbulb,
  Shield,
  Clock,
  Brain,
  Search
} from 'lucide-react'

// Removed fade-up variants to disable page load animations

const features = [
  {
    icon: MessageCircle,
    title: 'Social Listening',
    description: 'Monitor conversations about your brand across TikTok and Instagram in real-time'
  },
  {
    icon: ShoppingCart,
    title: 'Commerce Intelligence',
    description: 'Track product mentions, shopping intent, and purchase conversations'
  },
  {
    icon: Target,
    title: 'Price Intelligence',
    description: 'Monitor pricing discussions, competitor pricing strategies, and market positioning'
  },
  {
    icon: Users,
    title: 'Competitor Analysis',
    description: 'Track competitor mentions, strategies, and market share discussions'
  }
]

const capabilities = [
  {
    icon: Eye,
    title: 'Real-time Monitoring',
    description: 'Track mentions, hashtags, and conversations as they happen'
  },
  {
    icon: Activity,
    title: 'Sentiment Analysis',
    description: 'Understand how people feel about your brand and products'
  },
  {
    icon: PieChart,
    title: 'Trend Detection',
    description: 'Identify emerging trends and viral content early'
  },
  {
    icon: Lightbulb,
    title: 'Insight Generation',
    description: 'Transform raw data into actionable business intelligence'
  },
  {
    icon: Shield,
    title: 'Crisis Management',
    description: 'Get alerts for potential PR issues and negative sentiment'
  },
  {
    icon: Clock,
    title: 'Historical Analysis',
    description: 'Compare current performance with historical data'
  }
]

const metrics = [
  { label: 'Monthly Mentions', value: '128K', change: '+12.4%', changeColor: 'text-emerald-600', progress: 72 },
  { label: 'Positive Sentiment', value: '64%', change: '+3.1%', changeColor: 'text-emerald-600', progress: 64 },
  { label: 'Share of Voice', value: '27%', change: '+1.8%', changeColor: 'text-emerald-600', progress: 27 },
  { label: 'Active Creators', value: '3.2K', change: '+6.0%', changeColor: 'text-emerald-600', progress: 60 }
]

const mentionsData = [
  { day: 'D1', mentions: 1200 },
  { day: 'D2', mentions: 1600 },
  { day: 'D3', mentions: 1400 },
  { day: 'D4', mentions: 1800 },
  { day: 'D5', mentions: 1500 },
  { day: 'D6', mentions: 2100 },
  { day: 'D7', mentions: 1900 },
  { day: 'D8', mentions: 2300 },
  { day: 'D9', mentions: 2200 },
  { day: 'D10', mentions: 2600 },
  { day: 'D11', mentions: 2400 },
  { day: 'D12', mentions: 2800 }
]

export function DeepIntelligencePage() {
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-purple-400/10 to-transparent dark:from-purple-400/5 rounded-full -translate-y-8 translate-x-8" />
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Zap className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                Deep Intelligence
              </CardTitle>
              <p className="text-muted-foreground">Deep listening & commerce intelligence across all social platforms</p>
              
              <div className="flex flex-wrap gap-2 pt-2">
                <Badge variant="secondary" className="bg-muted/50 text-muted-foreground">
                  <Globe className="h-3.5 w-3.5 mr-1" />
                  All Socials
                </Badge>
                <Badge variant="secondary" className="bg-muted/50 text-muted-foreground">
                  <BarChart3 className="h-3.5 w-3.5 mr-1" />
                  Real-time Analytics
                </Badge>
                <Badge variant="secondary" className="bg-muted/50 text-muted-foreground">
                  <Zap className="h-3.5 w-3.5 mr-1" />
                  AI-Powered Insights
                </Badge>
              </div>
            </div>
            <Badge variant="secondary" className="text-xs">
              Pro-feature
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Main Description */}
      <div 
        className="space-y-6"
      >
        <Card className="border border-border/50 bg-card/50 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl flex items-center gap-2 text-foreground">
              <Lightbulb className="h-4 w-4 text-primary" />
              Transform Social Conversations into Business Intelligence
            </CardTitle>
            <CardDescription className="text-sm sm:text-base text-muted-foreground">
              Discover how much people are organically talking about your brand across socials 
              and how is that affecting price and competition strategy.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {metrics.map((m) => (
                <Card key={m.label} className="border border-border/50 bg-background">
                  <CardContent className="pt-4">
                    <div className="text-xs text-muted-foreground">{m.label}</div>
                    <div className="mt-1 flex items-baseline gap-2">
                      <div className="text-xl font-semibold text-foreground">{m.value}</div>
                      <div className={`text-xs font-medium ${m.changeColor}`}>{m.change}</div>
                    </div>
                    <div className="mt-3">
                      <Progress value={m.progress} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="border border-border/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-muted-foreground">Last 12 days</div>
                  <div className="text-xs font-medium">Mentions trend</div>
                </div>
                <ChartContainer
                  config={{ mentions: { label: 'Mentions', color: 'hsl(var(--primary))' } }}
                  className="h-40"
                >
                  <AreaChart data={mentionsData} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="fillMentions" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis dataKey="day" tickLine={false} axisLine={false} tickMargin={8} />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                    <Area
                      dataKey="mentions"
                      type="monotone"
                      stroke="hsl(var(--primary))"
                      fill="url(#fillMentions)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </AreaChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>

      {/* Core Features Grid */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 flex-shrink-0">
              <BarChart3 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">Core Capabilities</CardTitle>
              <CardDescription>
                Essential intelligence features for comprehensive social monitoring
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
              >
                <Card className="group hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-muted/40 flex-shrink-0">
                        <feature.icon className="h-4 w-4 text-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-sm font-semibold group-hover:text-primary transition-colors">
                          {feature.title}
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Advanced Features */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-50/50 dark:bg-green-950/20 flex-shrink-0">
              <Brain className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">Advanced Features</CardTitle>
              <CardDescription>
                Sophisticated analytics and AI-powered intelligence capabilities
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {capabilities.map((capability) => (
              <motion.div
                key={capability.title}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
              >
                <Card className="group hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-muted/40">
                        <capability.icon className="h-4 w-4 text-foreground" />
                      </div>
                      <CardTitle className="text-sm font-semibold group-hover:text-primary transition-colors">
                        {capability.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {capability.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* CTA Section */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 flex-shrink-0">
              <MessageCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">Ready to Transform Your Social Intelligence?</CardTitle>
              <CardDescription>
                Get custom intelligence and reporting tailored to your specific business needs
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-center">
          <Button className="shadow-sm">
            Talk to us
            <ArrowRight className="h-3.5 w-3.5 ml-2" />
          </Button>
          <p className="text-xs text-muted-foreground mt-3">
            Get personalized insights, custom dashboards, and strategic recommendations
          </p>
        </CardContent>
      </Card>
    </div>
  )
}


